<template>
  <div
    v-if="showBanner"
    class="fixed bottom-0 left-0 right-0 bg-[#F4F4F4] shadow-lg z-50 border-t border-t-[2px] border-t-[#131313]"
  >
    <div class="container py-6">
      <p class="text-[18px] xl:text-[22px] font-normal font-serif my-0 mb-4">
        {{ isEdit ? $t('cookiesAgree.titleCustomization') : $t('cookiesAgree.title') }}
      </p>
          
      <div class="flex flex-col lg:flex-row -mx-4">
        <div
          v-if="isEdit"
          class="flex-1 px-4 text-[14px] xl:text-[16px]"
        >
          <Accordion
            :value="0"
          >
            <AccordionPanel
              v-for="item, i in categories" 
              :key="i"
              :value="i"
              class="bg-white rounded relative p-4"
            >
              <AccordionHeader
                class="text-[18px] font-normal"
              >
                <template #toggleicon="elProps">
                  <i
                    class="icon icon-down-arrow transition-all absolute left-0 text-[20px]"
                    :class="elProps.active ? 'rotate-180' : ''"
                  ></i>
                </template>
                
                <div class="pl-10">
                  {{ item.title }}
                </div>
              </AccordionHeader>
              
              <div class="pl-8 text-[14px] absolute right-6 flex items-center mt-2">
                <template v-if="item.alwaysEnabled">
                  {{ $t('alwaysEnabled') }}
                </template>

                <template v-else>
                  <span class="pr-2">{{ cookieConsent[item.id] ? $t('enabled') : $t('disabled') }}</span>

                  <ToggleSwitch v-model="cookieConsent[item.id]" />
                </template>
              </div>

              <AccordionContent class="pt-4 pl-10">
                <p
                  class="m-0 font-extralight text-[14px] xl:text-[16px]"
                  v-html="item.descr"
                >
                </p>
              </AccordionContent>
            </AccordionPanel>
          </Accordion>
        </div>
        <div
          v-else
          class="flex-1 px-4 text-[14px] xl:text-[16px]"
        >
          <p
            class="mb-0"
            v-html="$t('cookiesAgree.descr')"
          ></p>
          <p
            class="mt-4 mb-0"
            v-html="$t('cookiesAgree.descr2', { privacyLink: confNavigation.system.find(n => n.id === 'privacy').link, copyrightLink: confNavigation.system.find(n => n.id === 'copyright').link })"
          ></p>
        </div>

        <div class="flex-0 pt-8 lg:pt-0 px-4 flex flex-row lg:flex-col space-x-4 lg:space-x-0 space-y-0 lg:space-y-4 lg:w-[300px]">
          <Button
            v-if="isEdit"
            class="uppercase font-normal w-full"
            :label="$t('cookiesAgree.acceptSelected')"
            severity="secondary"
            @click="acceptSelected"
          />
          <Button
            class="uppercase font-normal w-full"
            :label="$t('cookiesAgree.acceptAll')"
            severity="secondary"
            :variant="isEdit ? 'outlined' : ''"
            @click="acceptAll"
          />
          <Button
            v-if="!isEdit"
            class="uppercase font-normal w-full"
            :label="$t('cookiesAgree.customize')"
            severity="secondary"
            variant="outlined"
            @click="isEdit = true"
          />
          <Button
            class="uppercase font-normal w-full"
            :label="$t('cookiesAgree.rejectAll')"
            severity="secondary"
            variant="outlined"
            @click="rejectAll"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { $i18n: i18n, $setGtmConsent } = useNuxtApp()
const { confNavigation } = useConfig()
const { useCustomCookie } = useCustomTools()

const isEdit = ref(false)

const cookieConsent = useState('cookieConsent', () => ({
  necessary: true,
  functional: useCustomCookie('cookieConsent').value?.functional === false ? useCustomCookie('cookieConsent').value?.functional : true,
  performance: useCustomCookie('cookieConsent').value?.performance === false ? useCustomCookie('cookieConsent').value?.performance : true,
  analytics: useCustomCookie('cookieConsent').value?.analytics === false ? useCustomCookie('cookieConsent').value?.analytics : true,
  others: useCustomCookie('cookieConsent').value?.others === false ? useCustomCookie('cookieConsent').value?.others : true
}))

const showBanner = ref(useCustomCookie('cookieConsentAccepted').value !== true)

/*
* Accept Selected
*/
const acceptSelected = () => {
  useCustomCookie('cookieConsent').value = JSON.stringify(cookieConsent.value)
  useCustomCookie('cookieConsentAccepted').value = true
  showBanner.value = false
}

/*
* Accept All
*/
const acceptAll = () => {
  cookieConsent.value = {
    necessary: true,
    functional: true,
    performance: true,
    analytics: true,
    others: true
  }
  acceptSelected()
}

/*
* Reject Selected
*/
const rejectAll = () => {
  cookieConsent.value = {
    necessary: true,
    functional: false,
    performance: false,
    analytics: false,
    others: false
  }
  acceptSelected()
}

/*
* If Updated Cookies Consepr
*/
$setGtmConsent(cookieConsent.value)
watch(cookieConsent, (newA) => {
  $setGtmConsent(newA)
}, { deep: true })

/*
* Categories
*/
const categories = [
  {
    id: 'necessary',
    alwaysEnabled: true,
    title: i18n?.t('cookiesAgree.necessaryTitle'),
    descr: i18n?.t('cookiesAgree.necessaryDescr')
  },
  {
    id: 'functional',
    alwaysEnabled: false,
    title: i18n?.t('cookiesAgree.functionalTitle'),
    descr: i18n?.t('cookiesAgree.functionalDescr')
  },
  {
    id: 'performance',
    alwaysEnabled: false,
    title: i18n?.t('cookiesAgree.performanceTitle'),
    descr: i18n?.t('cookiesAgree.performanceDescr')
  },
  {
    id: 'analytics',
    alwaysEnabled: false,
    title: i18n?.t('cookiesAgree.analyticsTitle'),
    descr: i18n?.t('cookiesAgree.analyticsDescr')
  },
  {
    id: 'others',
    alwaysEnabled: false,
    title: i18n?.t('cookiesAgree.othersTitle'),
    descr: i18n?.t('cookiesAgree.othersDescr')
  }
]
</script>

<style scoped></style>